/**
 * Windows Event Log (.evtx) constants — the single source of truth for every
 * SpecTree layer that accepts, validates, or summarizes .evtx files (web intake,
 * API upload and summary routes, MCP FogBugz tools, and @spectree/evtx).
 *
 * Browser-safe on purpose: this module uses only Uint8Array and string helpers
 * (no Buffer, no node: imports), so the web bundle can import it.
 */
/** File extension for Windows Event Log files (lower case, with the dot). */
export const EVTX_FILE_EXTENSION = ".evtx";
/**
 * Largest .evtx file SpecTree accepts, in bytes (10 MiB). Matches every existing
 * upload path (global multipart limit, attach route, screenshot route), so it
 * changes no infrastructure. Larger files must be attached directly in FogBugz.
 */
export const EVTX_MAX_BYTES = 10 * 1024 * 1024;
/** The 8 magic bytes at offset 0 of every .evtx file: "ElfFile\0". */
const EVTX_MAGIC_BYTES = Object.freeze([
    0x45, 0x6c, 0x66, 0x46, 0x69, 0x6c, 0x65, 0x00,
]);
/**
 * The 8 magic bytes at offset 0 of every .evtx file ("ElfFile\0").
 *
 * Typed arrays cannot be frozen, so this is a copy: mutating it does not change
 * what {@link hasEvtxMagic} accepts.
 */
export const EVTX_FILE_MAGIC = Uint8Array.from(EVTX_MAGIC_BYTES);
/**
 * Suffix for a generated .evtx summary attachment. The summary for
 * `Lynx.evtx` is stored as `Lynx.evtx-summary.md`.
 */
export const EVTX_SUMMARY_SUFFIX = ".evtx-summary.md";
/** Maximum number of .evtx files a single intake session may attach. */
export const MAX_EVENT_LOGS_PER_SESSION = 3;
/**
 * True when `name` (a bare file name or a path with `/` or `\` separators) ends
 * in `.evtx`, compared case-insensitively on the basename. A basename that is
 * only `.evtx` (no stem) is rejected.
 */
export function isEvtxFileName(name) {
    const lastSeparator = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
    const baseName = name.slice(lastSeparator + 1);
    return (baseName.length > EVTX_FILE_EXTENSION.length &&
        baseName.toLowerCase().endsWith(EVTX_FILE_EXTENSION));
}
/**
 * Suffix FogBugz adds to the names of attachments it treats as risky
 * downloads: an uploaded `Lynx.evtx` is listed on the case as
 * `Lynx.evtx.unsafe`.
 */
export const FOGBUGZ_UNSAFE_SUFFIX = ".unsafe";
/** `name` without a trailing FogBugz `.unsafe` suffix (compared case-insensitively). */
export function withoutFogbugzUnsafeSuffix(name) {
    return name.toLowerCase().endsWith(FOGBUGZ_UNSAFE_SUFFIX)
        ? name.slice(0, -FOGBUGZ_UNSAFE_SUFFIX.length)
        : name;
}
/**
 * Copy number a browser adds to a file downloaded again into the same folder:
 * the ` (1)` in `Lynx.evtx (1)`. Someone who downloads a log and attaches it to
 * another case uploads that name, and FogBugz lists it as
 * `Lynx.evtx (1).unsafe`.
 */
const DOWNLOAD_COPY_NUMBER = /\s\(\d{1,3}\)$/;
/**
 * A FogBugz attachment name as the .evtx it was: a trailing `.unsafe` dropped
 * (see {@link withoutFogbugzUnsafeSuffix}), then a browser copy number after
 * `.evtx` dropped, so `Lynx.evtx (1).unsafe` becomes `Lynx.evtx`. Any other
 * name comes back with only the `.unsafe` dropped.
 */
export function fogbugzEvtxAttachmentBaseName(name) {
    const withoutUnsafe = withoutFogbugzUnsafeSuffix(name);
    const withoutCopy = withoutUnsafe.replace(DOWNLOAD_COPY_NUMBER, "");
    return withoutCopy !== withoutUnsafe && isEvtxFileName(withoutCopy) ? withoutCopy : withoutUnsafe;
}
/**
 * True when a FogBugz attachment name is an .evtx file: {@link isEvtxFileName}
 * after {@link fogbugzEvtxAttachmentBaseName} (a trailing `.unsafe` and a
 * browser copy number dropped). Use it for files read back from a case; a file
 * the person uploads from their computer keeps the strict
 * {@link isEvtxFileName} check.
 */
export function isFogbugzEvtxAttachmentName(name) {
    return isEvtxFileName(fogbugzEvtxAttachmentBaseName(name));
}
/**
 * True when `bytes` begins with the .evtx magic ("ElfFile\0"). Buffers shorter
 * than 8 bytes return false. Node Buffers are Uint8Arrays, so they work too.
 */
export function hasEvtxMagic(bytes) {
    if (bytes.length < EVTX_MAGIC_BYTES.length) {
        return false;
    }
    for (let i = 0; i < EVTX_MAGIC_BYTES.length; i++) {
        if (bytes[i] !== EVTX_MAGIC_BYTES[i]) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=constants.js.map